import React, { useCallback, useState } from 'react';

import { useTranslation } from '@lyearn/i18n';
import { cn } from '@lyearn/style';
import { Button, Divider, Stack, Typography } from '@lyearn/ui';
import { useAccessControl } from '@/components/AccessControl';
import BannerWithTitle from '@/components/EditorViewer/BannerWithTitle';
import { languageSelectorStyles } from '@/components/EditorViewer/BannerWithTitle/styles';
import { titleStyles } from '@/components/EditorViewer/styles';
import ProviderComponent from '@/components/Provider';
import { FeatureFlags, useFeatureFlags } from '@/helper/FeatureFlags';
import useParams from '@/hooks/useParams';
import { useUserContext } from '@/modules/auth/hooks/useUser';
import ContentAccessEvents from '@/modules/corporate/components/ContentAccessEvents';
import SelectLanguage from '@/modules/corporate/components/DetailPageComponents/DetailHero/components/Languages';
import useContentAccessed from '@/modules/corporate/hooks/useContentAccessed';
import useContentPerformance from '@/modules/corporate/hooks/useContentPerformance';
import useContentViewProgress from '@/modules/corporate/hooks/useContentViewProgress';
import useResourcePerformanceResponse from '@/modules/corporate/modules/course/views/CourseDetails/hooks/useResourcePerformanceResponse';
import { useUpdateContent } from '@/modules/corporate/modules/course/views/CourseViewer/components/MainContent/LessonMainContent/hooks/useUpdateContent';
import DatabaseForm from '@/modules/corporate/modules/database/views/FormView/components/DatabaseForm';
import { useAdminRoute } from '@/pages/corporate/routes/hooks/useAdminRoutes';
import {
  LanguageConfig,
  LanguageEnum,
  PerformanceStatusEnum,
  ResourceActions,
  ResourceSubjects,
  ResourceTypeEnum,
} from '@/types/schema';

import SurveyCompleteContent from './components/SurveyCompleteContent';
import useSurveyContent from './hooks/useSurveyContent';
import parseSurveyFormValues from './utils/parseSurveyFormValues';
import {
  surveyBannerStyles,
  surveyCompleteBannerStyles,
  surveyDetailsContentStyles,
} from './styles';
import { SurveyContentContainerProps } from './types';

const BANNER_CLASSES = {
  titleContainer: 'pb-56 xs:pb-24',
  title: cn(surveyDetailsContentStyles, 'w-full'),
  lessonTitle: 'xs:mx-24',
};

const SurveyContent: React.FC<SurveyContentContainerProps> = (props) => {
  const {
    surveyContent,
    setHeaderState,
    fields,
    parentContentId,
    onBack,
    backActionText,
    isEmbed,
  } = props;

  const { t } = useTranslation();

  const isAdminRoute = useAdminRoute();
  const { parentEntityId: pathId } = useParams<{
    parentEntityId: string;
  }>();
  const user = useUserContext();
  const {
    [FeatureFlags.DisableContentAccessEventOnCompletion]: disableContentAccessEventOnCompletion,
    [FeatureFlags.EnableMultiLanguageContent]: isMultiLanguageContentEnabled,
  } = useFeatureFlags();

  const { languageConfigs, languageType, defaultLanguageType } = surveyContent;

  const canConsume = useAccessControl(
    surveyContent.userPermissions,
    ResourceActions.Consume,
    ResourceSubjects.Resource,
  );

  const disableContentAccessEvents = Boolean(
    disableContentAccessEventOnCompletion &&
      surveyContent.performance?.status === PerformanceStatusEnum.Completed,
  );
  const shouldFetchPathPerformance = pathId && !isAdminRoute;
  const entityIds = shouldFetchPathPerformance ? [pathId] : [];
  const { data: pathPerformances } = useContentPerformance(entityIds, user.id, false);

  const pathPerformance = useResourcePerformanceResponse(pathId, pathPerformances);

  useContentViewProgress({
    entityId: surveyContent.id,
    pause: isAdminRoute,
    isContentVisited: !disableContentAccessEvents,
  });
  useContentAccessed({
    entityId: surveyContent.id,
    pause: isAdminRoute,
    sendContentAccessedEvent: !disableContentAccessEvents,
  });

  const [formikSubmit, setFormikSubmit] = useState();
  const { fetching, setBannerRef, onUpdateSurveyPerformance, onChangeValues, initialValues } =
    useSurveyContent(surveyContent.id, fields, setHeaderState, isEmbed);

  const isSurveyComplete = surveyContent.performance?.status === PerformanceStatusEnum.Completed;

  const onSubmit = useCallback(
    (values: any) => {
      const { fieldsData } = parseSurveyFormValues({ parentContentId, values, fields });
      onUpdateSurveyPerformance(
        { dbId: 'survey', fields: fieldsData },
        PerformanceStatusEnum.Completed,
      );
    },
    [fields, onUpdateSurveyPerformance, parentContentId],
  );

  const { onNext, nextContentName } = useUpdateContent(parentContentId ?? '', surveyContent.id);
  return (
    <>
      {pathPerformance ? ( // To increase the access count of parent path
        <ContentAccessEvents
          entityId={pathId}
          pause={isAdminRoute}
          performanceStatus={pathPerformance.status}
          recalibrationRequired={false}
        />
      ) : null}
      <Stack key={surveyContent.id} alignItems="center" className="flex-1" direction="col">
        <BannerWithTitle
          ref={setBannerRef}
          className={cn(
            'xs:mb-0',
            isSurveyComplete ? surveyCompleteBannerStyles : surveyBannerStyles,
          )}
          classes={BANNER_CLASSES}
          languageSelector={
            (languageConfigs?.length || 0) <= 1 || !isMultiLanguageContentEnabled || true ? null : (
              <SelectLanguage
                className={languageSelectorStyles}
                id={surveyContent.id}
                languageConfigs={languageConfigs as LanguageConfig[]}
                languageType={(languageType ?? defaultLanguageType) as LanguageEnum}
                resourceType={ResourceTypeEnum.Survey}
              />
            )
          }
          src={surveyContent.cover?.url ?? ''}
          title={!isSurveyComplete ? surveyContent.name : ''}
          yOffsetPercent={surveyContent.coverOffset || 0}
        />
        {isSurveyComplete ? (
          <SurveyCompleteContent
            backActionText={backActionText}
            className={surveyContent.cover?.url ? 'mt-56' : 'xxs:px-20 flex-1 justify-center'}
            nextContentName={nextContentName}
            onBack={onBack}
            onNext={onNext}
          />
        ) : (
          <Stack
            className={cn(
              'mb-40 w-full flex-shrink-0 flex-grow xs:px-20',
              surveyDetailsContentStyles,
            )}
            direction="col">
            {!surveyContent.cover?.url ? (
              <Typography
                className={cn('me-32 xs:me-0', titleStyles)}
                color="text-text-primary"
                variant="display-xxl">
                {surveyContent.name}
              </Typography>
            ) : null}
            {surveyContent.description ? (
              <>
                <Typography
                  className="mt-24 me-32 xs:me-0"
                  color="text-text-secondary"
                  variant="body-long-01">
                  {surveyContent.description}
                </Typography>
                <Divider
                  className="mt-20 w-full xs:mt-24 xs:me-20"
                  color="border-interactive-secondary"
                />
              </>
            ) : null}
            {surveyContent.sharedByWorkspace ? (
              <ProviderComponent className="mt-40" workspace={surveyContent.sharedByWorkspace} />
            ) : null}
            <DatabaseForm
              className="mt-20 xs:mt-24"
              fields={fields}
              initialValues={initialValues}
              setFormikSubmit={setFormikSubmit}
              onChange={onChangeValues}
              onSubmit={onSubmit}
            />
            <Stack className="mt-56">
              <Button loading={fetching} onClick={formikSubmit}>
                {t('Common:Actions.Submit')}
              </Button>
            </Stack>
          </Stack>
        )}
      </Stack>
    </>
  );
};

export default SurveyContent;
